import type { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";
import { useState } from "react";

/**
 * Page-by-page view of a cursor list (useInfiniteQuery with a `before` cursor), for DataTable's pager: Next fetches
 * the next server page when it isn't loaded yet; Previous shows a page already loaded. Going back to page one when
 * the filters change is keyed on `filterKey`. The API calls stay exactly the cursor calls they always were.
 */
export function useCursorPages<P, R>(list: UseInfiniteQueryResult<InfiniteData<P>, unknown>, rowsOf: (page: P) => R[], filterKey: string) {
  const [state, setState] = useState({ key: filterKey, page: 0 });
  const pages = list.data?.pages ?? [];
  const page = state.key === filterKey ? Math.min(state.page, Math.max(0, pages.length - 1)) : 0;
  const rows = pages[page] ? rowsOf(pages[page]) : [];
  const pageSize = Math.max(1, pages[0] ? rowsOf(pages[0]).length : 50);
  const onPageChange = (n: number) => {
    if (n < pages.length) return setState({ key: filterKey, page: n });
    void list.fetchNextPage().then(() => setState({ key: filterKey, page: n }));
  };
  return {
    rows,
    /** True while the first page or the next page loads. */
    loading: list.isPending || list.isFetchingNextPage,
    pagination: pages.length > 1 || list.hasNextPage ? { page, pageSize, hasMore: page < pages.length - 1 || !!list.hasNextPage, onPageChange } : undefined,
  };
}
